import React, { useEffect, useState } from 'react';
import { ButtonCancel, ButtonNew, CompositeView, MasterDetailView, useRouter } from '@digital/ui';
import CrudMaster from '@digital/ui/lib/components/CrudMasterDetailView/CrudMaster';
import CrudCreate from '@digital/ui/lib/components/CrudMasterDetailView/CrudCreate';
import CrudDetail from '@digital/ui/lib/components/CrudMasterDetailView/CrudDetail';
import NoItemSelected from '@digital/ui/lib/components/CrudMasterDetailView/NoItemSelected';
import { OrganogramaGestor } from '@app/api/organogramaGestor';

interface Props {
  getAllItems: () => Promise<OrganogramaGestor[] | undefined>;
  createItem?: (item: OrganogramaGestor) => Promise<any>;
  updateItem?: (item: OrganogramaGestor) => Promise<any>;
  titleExtractor: (item: OrganogramaGestor) => string;
  detailTitleExtractor: (item: OrganogramaGestor) => string;
  descriptionExtractor: (item: OrganogramaGestor) => string;
  filterPredicate: (item: OrganogramaGestor, value: string) => boolean;
  toolbar: React.ReactNode;
  children: React.ReactNode;
}

// Compose the same design-system CRUD primitives, with an import action on every route.
const OrganogramaMasterDetailView = (props: Props) => {
  const [items, setItems] = useState<OrganogramaGestor[]>([]);
  const [selectedId, setSelectedId] = useState<string>();
  const { rootUrl, pushRoot } = useRouter();

  useEffect(() => {
    let active = true;
    props.getAllItems().then(result => {
      if (active && result) setItems(result);
    });
    return () => {
      active = false;
    };
  }, [props.getAllItems]);

  const toolbar = (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
      {props.createItem && <ButtonNew linkTo={rootUrl('new')} aria-label="Novo gestor" />}
      {props.toolbar}
    </div>
  );

  return (
    <MasterDetailView
      master={
        <CrudMaster
          items={items}
          selectedId={selectedId}
          icon="business-employee_card"
          titleExtractor={props.titleExtractor}
          descriptionExtractor={props.descriptionExtractor}
          filterPredicate={props.filterPredicate}
        />
      }
      detail={
        <>
          <CompositeView toolbar={toolbar} onRender={() => setSelectedId(undefined)}>
            <NoItemSelected />
          </CompositeView>
          {props.createItem && (
            <CompositeView
              id="new"
              toolbar={
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, whiteSpace: 'nowrap' }}>
                  <ButtonCancel linkTo={rootUrl()} aria-label="Cancelar cadastro" />
                  {props.toolbar}
                </div>
              }>
              <CrudCreate
                onCreate={async (value: OrganogramaGestor) => {
                  const result = await props.createItem!(value);
                  if (result && result.valid !== false) pushRoot();
                  return result;
                }}>
                {props.children}
              </CrudCreate>
            </CompositeView>
          )}
          <CompositeView
            id="detail"
            path="/:id"
            toolbar={toolbar}
            onRender={({ match }) => setSelectedId(match.params.id)}>
            <CrudDetail
              selectedId={selectedId}
              masterItems={items}
              onUpdate={props.updateItem}
              titleExtractor={props.detailTitleExtractor}
              disableNotification>
              {props.children}
            </CrudDetail>
          </CompositeView>
        </>
      }
    />
  );
};

export default OrganogramaMasterDetailView;
